// CSV-import dialog for the Inventory (parts) module.
//
// Flow:
//   1. Admin drops a CSV file (or clicks the file picker).
//   2. Client-side parser normalises the file, validates the header
//      row and per-cell types, and shows a preview table + row-scoped
//      error list.
//   3. On Import, the parsed row set posts to `POST /parts/bulk`. The
//      server runs the whole batch in one DB transaction — either
//      every row lands or none.
//   4. Server-side 422s carry a `errors: [{row, name, message}]`
//      envelope so the frontend can re-render the same preview with
//      backend-flagged rows highlighted.
//
// FK columns (site_id, vendor_id, location_id) accept either a UUID
// or a human-readable name — the server does a case-insensitive
// lookup against the corresponding table. Names that don't exist
// bubble up as row-scoped errors.
//
// The parser is inline (no papaparse dependency) so the dialog stays
// self-contained. Same 40-line parser shape as SitesTab uses.

import { useMemo, useRef, useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, LinearProgress, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { BulkCreatePartRow } from '@cmms/api-client'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'

// ── CSV column set — kept in sync with backend `bulkCreatePartsSchema`.
const HEADERS = [
  'name',
  'description',
  'sku',
  'category',
  'quantity',
  'min_quantity',
  'unit_cost',
  'site',        // resolved to site_id via display_name lookup
  'vendor',      // resolved to vendor_id via name lookup
  'location',    // resolved to location_id via name lookup
  'customer',    // resolved to customer_id via name lookup; blank = active customer context (or shared)
  'last_restocked',
  'notes',
] as const

type ParsedRow = BulkCreatePartRow & { _row: number; _error?: string }

// ─── Component ───────────────────────────────────────────────────

export function ImportPartsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [rows, setRows] = useState<ParsedRow[]>([])
  // Server-flagged rows keyed by row number so we can highlight them
  // in the preview table after a 422.
  const [serverErrors, setServerErrors] = useState<Record<number, string>>({})

  const importM = useMutation({
    mutationFn: (payload: BulkCreatePartRow[]) => api.parts.bulkCreate(payload),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['parts'] })
      toast.success(`Imported ${r.created} part${r.created === 1 ? '' : 's'}`)
      resetAndClose()
    },
    onError: (err) => {
      // The bulk endpoint replies 422 with `{ errors: [{row, name,
      // message}] }` for per-row failures — surfaced on ApiError as
      // `.rowErrors` (not `.response.data`, which only exists on a raw
      // AxiosError; every api.* call here is normalized to ApiError
      // before it reaches onError).
      if (err instanceof ApiError && err.rowErrors?.length) {
        const map: Record<number, string> = {}
        for (const e of err.rowErrors) map[e.row] = e.message
        setServerErrors(map)
        toast.error(`${err.rowErrors.length} row${err.rowErrors.length === 1 ? '' : 's'} rejected — see below`)
        return
      }
      toast.error(err instanceof ApiError ? err.message : 'Import failed')
    },
  })

  function resetAndClose() {
    setFile(null); setRows([]); setParseError(null); setServerErrors({})
    onClose()
  }

  async function handleFilePicked(f: File | undefined | null) {
    if (!f) return
    setFile(f)
    setServerErrors({})
    setRows([])
    setParseError(null)
    const text = await f.text()
    const parsed = parsePartsCsv(text)
    if (parsed.headerError) {
      setParseError(parsed.headerError)
      return
    }
    setRows(parsed.rows)
  }

  const validRows = useMemo(
    () => rows.filter((r) => !r._error && !serverErrors[r._row]),
    [rows, serverErrors],
  )
  const invalidRows = useMemo(
    () => rows.filter((r) => r._error || serverErrors[r._row]),
    [rows, serverErrors],
  )
  const canImport = validRows.length > 0 && invalidRows.length === 0 && !importM.isPending

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, minHeight: '60vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <UploadFileOutlinedIcon sx={{ fontSize: 22, color: 'primary.main' }} />
        Import parts from CSV
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* Guidance strip */}
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              How this works
            </Typography>
            <Typography sx={{ fontSize: 12.5 }} component="div">
              • Download the template, fill in your parts, and drop the CSV back in.<br />
              • <strong>Required column:</strong> <code>name</code>. Every other column is optional.<br />
              • For <code>site</code>, <code>vendor</code>, <code>location</code>, and <code>customer</code>, you can put
              either a name (case-insensitive) or a UUID. Names that don't already exist will be rejected.<br />
              • Leave <code>customer</code> blank to use whichever customer is currently active (or shared
              across every customer, if none is active) — set it per row to scope specific parts differently.<br />
              • The import is transactional — if any row is invalid, nothing is inserted.
            </Typography>
          </Alert>

          {/* Template + upload row */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
            <Button variant="outlined" startIcon={<DownloadIcon />}
              onClick={downloadCsvTemplate}
              sx={{ borderRadius: 2, fontWeight: 600 }}>
              Download CSV template
            </Button>
            <Button variant="contained" startIcon={<UploadFileOutlinedIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ borderRadius: 2, fontWeight: 600 }}>
              {file ? 'Choose another file' : 'Upload CSV'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => handleFilePicked(e.target.files?.[0])}
            />
            {file && (
              <Chip label={file.name} onDelete={() => resetAndClose()}
                sx={{ alignSelf: 'center', fontWeight: 600 }} />
            )}
          </Stack>

          {parseError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {parseError}
            </Alert>
          )}

          {rows.length > 0 && (
            <>
              <Divider />
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Preview</Typography>
                <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                  size="small" color="success"
                  label={`${validRows.length} valid`}
                  sx={{ fontWeight: 700 }} />
                {invalidRows.length > 0 && (
                  <Chip icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
                    size="small" color="error"
                    label={`${invalidRows.length} invalid`}
                    sx={{ fontWeight: 700 }} />
                )}
              </Stack>

              <Box sx={{
                borderRadius: 2, border: '1px solid', borderColor: 'divider',
                maxHeight: 380, overflow: 'auto',
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>Category</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>Min</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>Cost</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>Site</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>Vendor</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: 'background.paper' }}>Customer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => {
                      const err = r._error || serverErrors[r._row]
                      return (
                        <TableRow key={r._row}
                          sx={{ bgcolor: err ? '#FEF2F2' : 'transparent' }}>
                          <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r._row}</TableCell>
                          <TableCell sx={{ fontSize: 12.5, fontWeight: 600 }}>
                            {r.name || '—'}
                            {err && (
                              <Typography sx={{ fontSize: 11, color: '#DC2626', mt: 0.25 }}>
                                {err}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.sku ?? '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.category ?? '—'}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 12 }}>{r.quantity ?? 0}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 12 }}>{r.min_quantity ?? 0}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 12 }}>
                            {r.unit_cost != null ? r.unit_cost : '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.site_id ?? '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.vendor_id ?? '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.location_id ?? '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{r.customer_id ?? 'Active customer'}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}

          {importM.isPending && <LinearProgress />}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained"
          disabled={!canImport}
          onClick={() => importM.mutate(validRows.map(rowToRequest))}
          sx={{ px: 3, fontWeight: 700, borderRadius: 2 }}>
          {importM.isPending ? 'Importing…' : `Import ${validRows.length} part${validRows.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Strip the local `_row` / `_error` fields before posting. */
function rowToRequest(r: ParsedRow): BulkCreatePartRow {
  const { _row, _error, ...rest } = r
  return rest
}

// ─── Template download ───────────────────────────────────────────

function downloadCsvTemplate() {
  const header = HEADERS.join(',')
  const sample = [
    // Show admins what a filled-out row looks like — comments in the
    // notes column are safe because the parser strips outer quotes.
    // `customer` is left blank in every sample row — that's the
    // common case (use whichever customer is active); a row can set
    // it explicitly (name or UUID, same as site/vendor/location) to
    // scope just that part somewhere else.
    'Air filter,Standard 10x10 pleated filter,AF-1010,Filters,50,10,4.25,Warehouse A,Acme Supply,Storage Room 2,,2026-06-01,"Restock every 30 days"',
    'Hydraulic oil,ISO 46 hydraulic fluid,HO-46,Fluids,12,5,18.90,Warehouse B,,Chemical Store,,,',
    'Belt drive,Standard V-belt,BD-A38,Mechanical,8,3,7.50,,Acme Supply,,,,',
    // A row using UUIDs instead of names — helpful for admins who
    // already know the target IDs and want zero server-side lookups.
    'LED bulb,20W E27 bulb,LED-20,Electrical,120,25,3.15,3f2d7f3e-1a2b-4c3d-8e5f-9a1b2c3d4e5f,,,,,',
  ].join('\n')
  const blob = new Blob([`${header}\n${sample}\n`], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = 'parts-import-template.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ─── CSV parser ──────────────────────────────────────────────────
//
// Same shape as SitesTab uses so parsing behaviour is consistent
// across import surfaces. Strips BOM, normalises line endings,
// respects double-quoted values with escaped quotes.

function parsePartsCsv(text: string): { rows: ParsedRow[]; headerError: string | null } {
  const clean = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n').trim()
  if (clean.length === 0) return { rows: [], headerError: 'The file is empty.' }
  const lines = clean.split('\n').filter((l) => l.trim().length > 0)
  if (lines.length < 2) return { rows: [], headerError: 'Need at least a header row + one data row.' }

  const header = parseCsvLine(lines[0]!).map((h) => h.trim().toLowerCase())
  if (!header.includes('name')) {
    return { rows: [], headerError: 'Header row must include a "name" column.' }
  }
  const idx = (col: string) => header.indexOf(col)

  const rows: ParsedRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]!)
    const row: ParsedRow = {
      _row: i + 1,
      name: pick(cells, idx('name')) ?? '',
    }
    const description = pick(cells, idx('description'))
    if (description != null) row.description = description
    const sku = pick(cells, idx('sku'))
    if (sku != null) row.sku = sku
    const category = pick(cells, idx('category'))
    if (category != null) row.category = category
    const notes = pick(cells, idx('notes'))
    if (notes != null) row.notes = notes
    const lastRestocked = pick(cells, idx('last_restocked'))
    if (lastRestocked != null) row.last_restocked = lastRestocked
    // FK columns — server resolves name → id.
    const site = pick(cells, idx('site'))
    if (site != null) row.site_id = site
    const vendor = pick(cells, idx('vendor'))
    if (vendor != null) row.vendor_id = vendor
    const location = pick(cells, idx('location'))
    if (location != null) row.location_id = location
    const customer = pick(cells, idx('customer'))
    if (customer != null) row.customer_id = customer

    // Numeric fields — blank → undefined so the backend picks the
    // column DEFAULT; non-numeric strings surface as a row error.
    try {
      row.quantity     = numeric(cells, idx('quantity'),     'quantity')     ?? undefined
      row.min_quantity = numeric(cells, idx('min_quantity'), 'min_quantity') ?? undefined
      row.unit_cost    = numeric(cells, idx('unit_cost'),    'unit_cost')    ?? undefined
    } catch (e) {
      row._error = (e as Error).message
    }

    // Row-scoped validation the server also enforces; catching it
    // client-side lets us block the Import button without a round-trip.
    if (!row.name || row.name.trim().length === 0) {
      row._error = row._error ?? 'Name is required'
    }
    rows.push(row)
  }
  return { rows, headerError: null }
}

function pick(cells: string[], i: number): string | null {
  if (i < 0) return null
  const v = cells[i]
  if (v == null) return null
  const s = v.trim()
  return s.length === 0 ? null : s
}

function numeric(cells: string[], i: number, label: string): number | null {
  if (i < 0) return null
  const raw = cells[i]
  if (raw == null) return null
  const s = raw.trim()
  if (s.length === 0) return null
  const n = Number(s)
  if (!Number.isFinite(n)) throw new Error(`${label} must be a number (got "${s}")`)
  return n
}

/** Parse a single CSV line honouring double-quoted values and
 *  RFC-4180-style escaped quotes (`""` → `"`). */
function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ }
        else { quoted = false }
      } else {
        cur += ch
      }
    } else {
      if (ch === '"') quoted = true
      else if (ch === ',') { out.push(cur); cur = '' }
      else cur += ch
    }
  }
  out.push(cur)
  return out
}
